import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { identity, isEqual } from "lodash";
import type { IRule, IRuleItem, MultiIncludeExcludeWebsitesProps } from "./types";
import { createNewRow, MultiIncludeExcludeWebsitesTextField } from "./utils";
import { MultiIncludeExcludePopupContent } from "./MultiIncludeExcludePopupContent";
import { allTrackers } from "app/services/track/track";

export const MultiIncludeExcludeWebsitesInline: FC<Omit<MultiIncludeExcludeWebsitesProps, "getButtonText">> = ({
    rules: initialRules,
    onMultiIncludeExcludeChange,
    maxRules = 3,
    maxWebsitesPerRule = 20,
    withMatchType = true,
}) => {
    const contentContainerRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [rules, setRules] = useState<IRule[]>(initialRules ?? [createNewRow()]);

    useEffect(() => {
        if (!rules.length) {
            setRules([createNewRow()]);
        } else if (!isEqual(initialRules, rules)) {
            const rulesToNotifyOn = rules.filter((rule) => rule.items.length);
            onMultiIncludeExcludeChange(rulesToNotifyOn);
        }
    }, [rules]);

    const addRule = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", "Add rule");
        setRules([...rules, createNewRow()]);
        queueMicrotask(() => {
            contentContainerRef.current.scrollTop = contentContainerRef.current.scrollHeight;
        });
    };

    const deleteRule = (index: number) => {
        setRules([...rules.slice(0, index), ...rules.slice(index + 1)]);
    };

    const onRowTypeChange = (index: number, rule: Partial<Pick<IRule, "matchType" | "type">>) => {
        setRules([...rules.slice(0, index), createNewRow({ ...rules[index], ...rule }), ...rules.slice(index + 1)]);
    };

    const onWebsiteSelected = (website: string, index: number) => {
        // lowercase the string
        website = website?.toLowerCase?.();
        // remove http and www from the beginning of the string
        website = website?.replace?.(/^(https?:\/\/)?(www\.)?/i, "");
        if (!website) {
            return;
        }
        const trackWebsiteAdd = (website: string) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `keyword added/${website || ""}`);
        };
        const commaSeparatedItems = website
            .split(",")
            .map((item) => item.trim())
            .filter(identity);
        const uniqueCommaSeparatedItems = Array.from(new Set(commaSeparatedItems));
        trackWebsiteAdd(uniqueCommaSeparatedItems.join(","));

        const removeDuplicates = (items: IRuleItem[]) => {
            return items.filter(
                (item, index, self) => index === self.findIndex((t) => t.type === item.type && t.value === item.value),
            );
        };

        if (uniqueCommaSeparatedItems.length > 1) {
            setRules((currentRules) => {
                currentRules[index].items.push(
                    ...uniqueCommaSeparatedItems.map((value) => ({ type: "website", value }) as const),
                );
                currentRules[index].items = removeDuplicates(currentRules[index].items);
                currentRules[index].items = currentRules[index].items.slice(0, maxWebsitesPerRule);
                return [...currentRules];
            });
        } else if (website.trim().length) {
            setRules((currentRules) => {
                currentRules[index].items.push({ type: "website", value: website });
                currentRules[index].items = removeDuplicates(currentRules[index].items);
                return [...currentRules];
            });
        }
    };

    const onRemoveWebsite = (website: string, type: "website", _, index: number) => {
        setRules((currentRules) => {
            currentRules[index].items = currentRules[index].items.filter(
                (item) => item.type === type && item.value !== website,
            );
            return [...currentRules];
        });
    };

    return (
        <MultiIncludeExcludePopupContent
            fieldType="websites"
            rules={rules}
            maxItemsPerRule={maxWebsitesPerRule}
            addRow={addRule}
            deleteRow={deleteRule}
            onRowTypeChange={onRowTypeChange}
            onItemSelected={onWebsiteSelected}
            onRemoveItem={onRemoveWebsite}
            maxRules={maxRules}
            setContainerRef={(ref) => (contentContainerRef.current = ref)}
            withHeader={false}
            withFooter={false}
            withMatchType={withMatchType}
        >
            {MultiIncludeExcludeWebsitesTextField}
        </MultiIncludeExcludePopupContent>
    );
};
